"use client";

import Link from "next/link";

import { useMagnetUnlock } from "@/components/ownly/MagnetUnlock";
import { RevealOnScroll } from "@/components/ownly/RevealOnScroll";

export type GuideEntry = {
  title: string;
  paragraphs: readonly string[];
};

/**
 * The guide, laid out across the whole grid and cut off until an address is given.
 *
 * TWO FAULTS FIXED HERE, BOTH REPORTED BY TOM WITH SCREENSHOTS.
 *
 * One: it rendered every paragraph of all eight entries, directly under a form offering to
 * email him the guide. Nobody had any reason to fill the form in. Now the first paragraph
 * of each entry is public, which is the problem being described and is the part worth
 * indexing, and the three that say what to do about it need the form.
 *
 * Two: it sat inside the seven-of-twelve column that holds the sticky form, so once you
 * scrolled past the form the text ran on with the right two fifths of the page empty
 * beside it. It is its own full-width block now, and the width is used the way the rest of
 * the site uses it: the number and title in the left columns, the prose in the right, one
 * hairline between entries. The measure stays on the paragraphs where it belongs.
 */
export function GuideBody({ entries }: { entries: readonly GuideEntry[] }) {
  const { unlocked } = useMagnetUnlock();

  return (
    <div className="mt-16 md:mt-20">
      <div className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-2 border-t border-white/[0.12] pt-8">
        <h2 data-consistency-ok className="ownly-label text-ownly-accent-text">
          The guide
        </h2>
        <p className="ownly-label text-white/55">
          {entries.length} jobs · {unlocked ? "all of it" : "first part free"}
        </p>
      </div>

      <div>
        {entries.map((entry, i) => {
          const shown = unlocked ? entry.paragraphs : entry.paragraphs.slice(0, 1);
          return (
            <RevealOnScroll key={entry.title} delayMs={Math.min(i * 40, 200)}>
              <article className="grid gap-3 border-t border-white/[0.1] py-8 md:grid-cols-12 md:gap-10">
                <h3 className="ownly-display ownly-card-title flex items-baseline gap-4 text-white md:col-span-4">
                  <span
                    className="shrink-0 ownly-label tabular-nums text-white/55"
                    aria-hidden
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="ownly-sentence">{entry.title}</span>
                </h3>
                <div className="space-y-3.5 md:col-span-8">
                  {shown.map((para) => (
                    <p
                      key={para.slice(0, 40)}
                      className="ownly-body leading-[27px] text-white/85"
                    >
                      {para}
                    </p>
                  ))}
                  {/* A marker, not a sentence. The first version wrote the same explaining
                      line under all eight entries, which is a wall of identical copy and
                      reads as filler. The band at the bottom explains it once. */}
                  {!unlocked && entry.paragraphs.length > 1 ? (
                    <p className="ownly-label text-white/55">
                      {entry.paragraphs.length - 1} more parts in the guide
                    </p>
                  ) : null}
                </div>
              </article>
            </RevealOnScroll>
          );
        })}
      </div>

      {!unlocked ? (
        <div className="mt-10 rounded-2xl border border-ownly-accent/28 bg-gradient-to-r from-ownly-accent/[0.12] via-white/[0.03] to-ownly-surface/80 p-6 sm:flex sm:items-center sm:justify-between sm:gap-8 sm:p-8">
          <div className="min-w-0 flex-1">
            <h2 className="ownly-display ownly-card-title text-white">
              That is the problem. The guide is the fix.
            </h2>
            <p className="ownly-body mt-3 leading-[27px] text-white/85">
              You have read what each job costs you. What it looks like when it is handled,
              what actually changes, and the part worth knowing before you pay anyone to
              build it are the rest. Put your address in and it opens here straight away, as
              well as landing in your inbox to keep.
            </p>
          </div>
          <Link
            href="#lead-magnet-form"
            className="ownly-control ownly-btn-primary mt-6 inline-flex h-12 shrink-0 items-center justify-center rounded-full bg-ownly-accent px-6 text-white shadow-[var(--ownly-cta-shadow)] transition-[box-shadow,transform,background-color] duration-300 ease-out hover:-translate-y-0.5 hover:bg-[#3d8aff] active:translate-y-0 active:scale-[0.98] sm:mt-0"
          >
            Send it to me
          </Link>
        </div>
      ) : null}
    </div>
  );
}
